import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSplitViewAwareParams } from '@/hooks/useSplitViewAwareParams';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useTypesDataContext } from '@/providers/TypesDataProvider';
import { useToast } from '@/hooks/use-toast';
import { useTabContext } from '@/contexts/TabContext';
import { useTabTitleSync } from '@/hooks/useTabTitleSync';
import { useConfirmation } from '@/hooks/use-confirmation';
import {
  TypeItem,
  TypeElement,
  createType,
  createTypeElement,
  updateTypeElement,
  deleteTypeElement,
  updateType,
  deleteType,
} from '@/lib/stores/typesStore';
import { PageLoadingState } from '@/components/states/PageLoadingState';
import { PageEmptyState } from '@/components/states/PageEmptyState';
import { InlineElementEditor } from './components/InlineElementEditor';

// Generate a temporary ID for local elements
const generateTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export default function VariableDetailPage() {
  const { id: variableId } = useSplitViewAwareParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { selectedGame } = useSimplifiedUserData();
  const { types, isLoading: typesLoading, refreshTypes } = useTypesDataContext();
  const { toast } = useToast();
  const { updateTabTitle, updateTabUnsavedChanges, tabs } = useTabContext();
  const { confirmation } = useConfirmation();

  // Check if this is a new variable (not yet saved to server)
  const isNewVariable = variableId === 'new';
  const folderId = searchParams.get('folder');

  // Local draft state for new variables
  const [draftVariable, setDraftVariable] = useState<TypeItem | null>(() => {
    if (isNewVariable && selectedGame) {
      return {
        id: 'new',
        game_id: selectedGame.id,
        name: 'Untitled',
        element_type: 'string',
        data: [],
        folder: folderId || undefined,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
    }
    return null;
  });

  // Initialize draft when game becomes available (for new variables)
  React.useEffect(() => {
    if (isNewVariable && selectedGame && !draftVariable) {
      setDraftVariable({
        id: 'new',
        game_id: selectedGame.id,
        name: 'Untitled',
        element_type: 'string',
        data: [],
        folder: folderId || undefined,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }
  }, [isNewVariable, selectedGame, draftVariable, folderId]);

  const existingVariable = variableId && variableId !== 'new'
    ? types?.find(t => t.id === variableId) 
    : null;

  // Use draft for new variables, otherwise use existing
  const selectedVariable = isNewVariable ? draftVariable : existingVariable;

  // Check if we should start in edit mode
  const initialEditMode = isNewVariable || location.state?.editMode === true;

  const allGameVariables = useMemo(() => {
    if (!selectedGame || !types) return [];
    return types.filter((t) => t.game_id === selectedGame.id);
  }, [types, selectedGame]);

  // Sync tab title with variable name
  useTabTitleSync(selectedVariable?.name || 'New Variable');

  // --- Handlers for NEW (draft) variables ---
  
  const handleDraftElementCreate = useCallback((element: Omit<TypeElement, 'id'>) => {
    if (!draftVariable) return;
    
    const newElement: TypeElement = {
      ...element,
      id: generateTempId(),
    };
    
    setDraftVariable(prev => prev ? {
      ...prev,
      data: [...(prev.data || []), newElement],
    } : null);
  }, [draftVariable]);

  const handleDraftElementUpdate = useCallback((element: TypeElement) => {
    if (!draftVariable) return;
    
    setDraftVariable(prev => prev ? {
      ...prev,
      data: (prev.data || []).map(el => el.id === element.id ? element : el),
    } : null);
  }, [draftVariable]);

  const handleDraftElementDelete = useCallback((elementId: string) => {
    if (!draftVariable) return;
    
    setDraftVariable(prev => prev ? {
      ...prev,
      data: (prev.data || []).filter(el => el.id !== elementId),
    } : null);
  }, [draftVariable]);

  const handleDraftVariableUpdate = useCallback((updates: Partial<TypeItem>) => {
    if (!draftVariable) return;
    
    setDraftVariable(prev => prev ? {
      ...prev,
      ...updates,
    } : null);
  }, [draftVariable]);

  const handleDraftVariableDelete = useCallback(() => {
    // For draft variables, just navigate back without saving
    navigate('/variables');
  }, [navigate]);

  const handleDraftVariableSave = useCallback(async (finalName?: string) => {
    if (!draftVariable || !selectedGame) return;
    
    try {
      // Create the variable on the server
      const createdVariable = await createType({
        game_id: selectedGame.id,
        name: finalName || draftVariable.name, // Use finalName if provided, otherwise use draft name
        element_type: draftVariable.element_type,
        description: draftVariable.description,
        group: draftVariable.group_name || undefined,
        data: draftVariable.data || [], // Elements will be created separately
        folder: draftVariable.folder,
      }, toast);

      // Create all elements for this variable
      if (draftVariable.data && draftVariable.data.length > 0) {
        for (const element of draftVariable.data) {
          await createTypeElement({
            type_id: createdVariable.id,
            name: element.name,
            description: element.description,
            valueType: element.valueType,
            numericValue: element.numericValue,
            rangeMin: element.rangeMin,
            rangeMax: element.rangeMax,
            increment: element.increment,
            linkedVariableId: element.linkedVariableId,
            linkedElementId: element.linkedElementId,
          }, toast);
        }
      }

      await refreshTypes();

      toast({
        title: 'Success',
        description: 'Variable created successfully',
      });

      // Clear tab unsaved changes BEFORE navigation to prevent stale hasUnsavedChanges
      // from persisting through the component remount (key change causes old editor to unmount,
      // losing its cleanup, while the tab retains the old unsaved changes flag)
      const activeTab = tabs.find(t => t.isActive);
      if (activeTab) {
        updateTabUnsavedChanges(activeTab.id, false);
      }

      // Navigate to the real variable page
      navigate(`/variables/${createdVariable.id}`, { replace: true });
    } catch (error) {
      console.error("Error creating variable:", error);
      toast({
        title: 'Error',
        description: 'Failed to create variable',
        variant: 'destructive',
      });
    }
  }, [draftVariable, selectedGame, toast, refreshTypes, navigate, tabs, updateTabUnsavedChanges]);

  // --- Handlers for EXISTING variables ---

  const handleCreateElement = useCallback(async (element: Omit<TypeElement, 'id'>) => {
    if (!existingVariable) return;
    
    try {
      await createTypeElement(
        {
          type_id: existingVariable.id,
          name: element.name,
          description: element.description,
          valueType: element.valueType,
          numericValue: element.numericValue,
          rangeMin: element.rangeMin,
          rangeMax: element.rangeMax,
          increment: element.increment,
          linkedVariableId: element.linkedVariableId,
          linkedElementId: element.linkedElementId,
        },
        toast
      );
      
      refreshTypes();
    } catch (error) {
      console.error("Error creating element:", error);
    }
  }, [existingVariable, toast, refreshTypes]);

  const handleUpdateElement = useCallback(async (element: TypeElement) => {
    if (!existingVariable) return;
    
    try {
      await updateTypeElement(
        {
          id: element.id,
          type_id: existingVariable.id,
          name: element.name,
          description: element.description,
          valueType: element.valueType,
          numericValue: element.numericValue,
          rangeMin: element.rangeMin,
          rangeMax: element.rangeMax,
          increment: element.increment,
          linkedVariableId: element.linkedVariableId,
          linkedElementId: element.linkedElementId,
        },
        toast
      );
      
      refreshTypes();
    } catch (error) {
      console.error("Error updating element:", error);
    }
  }, [existingVariable, toast, refreshTypes]);

  const handleDeleteElement = useCallback(async (elementId: string) => {
    if (!existingVariable) return;
    
    try {
      await deleteTypeElement(elementId, existingVariable.id, toast);
      refreshTypes();
    } catch (error) {
      console.error("Error deleting element:", error);
    }
  }, [existingVariable, toast, refreshTypes]);

  const handleVariableUpdate = useCallback(async (updates: Partial<TypeItem>) => {
    if (!existingVariable || !selectedGame) return;
    
    try {
      await updateType(
        {
          ...existingVariable,
          ...updates,
          data: existingVariable.data || [],
        },
        toast
      );
      
      refreshTypes();
    } catch (error) {
      console.error("Error updating variable:", error);
    }
  }, [existingVariable, selectedGame, toast, refreshTypes]);

  const handleVariableDelete = useCallback(async () => {
    if (!existingVariable || !selectedGame) return;

    const confirmed = await new Promise<boolean>((resolve) => {
      confirmation({
        title: 'Delete Variable',
        description: `Are you sure you want to delete the variable "${existingVariable.name}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        variant: 'destructive',
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) return;

    try {
      await deleteType(existingVariable.id, selectedGame.id, toast);
      refreshTypes();
      
      toast({
        title: 'Success',
        description: 'Variable deleted successfully',
      });

      // Navigate back to variables page
      navigate('/variables');
    } catch (error) {
      console.error("Error deleting variable:", error);
      toast({
        title: 'Error',
        description: 'Failed to delete variable',
        variant: 'destructive',
      });
    }
  }, [existingVariable, selectedGame, toast, refreshTypes, navigate, confirmation]);

  if (typesLoading && !isNewVariable) {
    return <PageLoadingState message="Loading variable..." />;
  }

  if (!selectedGame) {
    return (
      <PageEmptyState
        icon="🎮"
        title="No Game Selected"
        description="Please select a game to view variables."
      />
    );
  }

  if (!selectedVariable) {
    return (
      <PageEmptyState
        icon="❓"
        title="Variable Not Found"
        description="The requested variable could not be found."
      />
    );
  }

  return (
    <div className="h-full overflow-auto">
      <InlineElementEditor
        variable={selectedVariable}
        allVariables={allGameVariables}
        gameId={selectedGame.id}
        onElementCreate={isNewVariable ? handleDraftElementCreate : handleCreateElement}
        onElementUpdate={isNewVariable ? handleDraftElementUpdate : handleUpdateElement}
        onElementDelete={isNewVariable ? handleDraftElementDelete : handleDeleteElement}
        onVariableUpdate={isNewVariable ? handleDraftVariableUpdate : handleVariableUpdate}
        onVariableDelete={isNewVariable ? handleDraftVariableDelete : handleVariableDelete}
        onVariableSave={isNewVariable ? handleDraftVariableSave : undefined}
        initialEditMode={initialEditMode}
        isNewVariable={isNewVariable}
      />
    </div>
  );
}
